'use client';

// @boty/ui - useMediaQuery hook
// Responsive breakpoint hook using MUI's useMediaQuery
//
// NOTE: These hooks use { noSsr: true } which means:
// - Server always returns false (no window available)
// - Client evaluates the actual media query
// - This can cause a brief flash if content differs between mobile/desktop
// - For critical layout differences, consider using CSS media queries instead
// - This trade-off avoids hydration mismatches in most cases

import { useTheme } from '@mui/material/styles';
import muiUseMediaQuery from '@mui/material/useMediaQuery';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * useMediaQuery - Responsive breakpoint hook
 *
 * @param query - CSS media query string or breakpoint-based query
 * @returns boolean indicating if query matches
 *
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const isMobile = useMediaQuery('(max-width:600px)');
 *   const isTablet = useMediaQuery('(min-width:600px) and (max-width:960px)');
 *
 *   return (
 *     <div>
 *       {isMobile ? <MobileView /> : <DesktopView />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMediaQuery(query: string): boolean {
  return muiUseMediaQuery(query, { noSsr: true });
}

/**
 * useBreakpoint - Check if viewport is at or above a specific breakpoint
 *
 * @param breakpoint - MUI breakpoint ('xs', 'sm', 'md', 'lg', 'xl')
 * @returns boolean indicating if viewport is at or above breakpoint
 *
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const isDesktop = useBreakpoint('md'); // true if >= 900px
 *   const isLargeScreen = useBreakpoint('lg'); // true if >= 1200px
 *
 *   return isDesktop ? <DesktopNav /> : <MobileNav />;
 * }
 * ```
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  const theme = useTheme();
  return muiUseMediaQuery(theme.breakpoints.up(breakpoint), { noSsr: true });
}

/**
 * useIsMobile - Check if viewport is mobile-sized (< 600px)
 *
 * @returns boolean indicating if viewport is mobile
 *
 * @example
 * ```tsx
 * function Header() {
 *   const isMobile = useIsMobile();
 *   return isMobile ? <MobileHeader /> : <DesktopHeader />;
 * }
 * ```
 */
export function useIsMobile(): boolean {
  const theme = useTheme();
  return muiUseMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
}
